import { useState } from 'react';
import {
  ArrowRight,
  ArrowUpRight,
  CalendarDays,
  Check,
  Clock3,
  Dumbbell,
  Mail,
  MapPin,
  Menu,
  ShieldCheck,
  Smartphone,
  Target,
  Users,
  X,
} from 'lucide-react';

const HERO_IMAGE = '/assets/blue-floor-hero.webp';
const SPACE_IMAGE = '/assets/blue-floor-facility.webp';
const BOOKING_EMAIL = 'bluefloorgym@gmail.com';
// Replace this one booking destination when online scheduling is live.
const BOOKING_URL = 'mailto:bluefloorgym@gmail.com?subject=Court%20booking';
const MAP_EMBED_URL = 'https://www.google.com/maps?q=5960+Telegraph+Ave,+Oakland,+CA+94609&output=embed';

const navItems = [
  ['Training use', '#how'],
  ['Facility', '#space'],
  ['Pricing', '#pricing'],
  ['Trainers', '#trainers'],
  ['FAQ', '#faq'],
] as const;

const trainingUses = [
  {
    number: '01',
    icon: Target,
    title: 'Individual shooting',
    copy: 'Use a private indoor session for mechanics, shot volume, and focused repetition.',
  },
  {
    number: '02',
    icon: Dumbbell,
    title: 'Skills workouts',
    copy: 'Train footwork, handles, finishing, and basketball-specific movement without a public run around you.',
  },
  {
    number: '03',
    icon: Smartphone,
    title: 'Private coaching',
    copy: 'Bring a coach or work one-on-one in a dedicated training bay built for instruction and reps.',
  },
  {
    number: '04',
    icon: Users,
    title: 'Small-group training',
    copy: 'Keep a focused group within the five-person maximum for the space.',
  },
] as const;

const faqs = [
  {
    q: 'What is Blue Floor?',
    a: 'Blue Floor is a private indoor basketball training space in Oakland—not a public full-size gym—for focused individual and small-group work.',
  },
  {
    q: 'What can I use the space for?',
    a: 'Individual shooting, basketball skills workouts, private coaching, and small-group training, subject to the five-person maximum and gym rules.',
  },
  {
    q: 'Is this a full-size court?',
    a: 'No. The training surface is 29 ft × 46 ft. It is designed for shooting, skill work, 1-on-1, and 2-on-2—not full-court runs.',
  },
  {
    q: 'What is in the facility?',
    a: 'The space has three hoops, a blue cushioned sport floor, and a shooting machine. Regulars include shooting-machine use; drop-in and trainer sessions can use it by scanning the QR code on the machine.',
  },
  {
    q: 'What hours can I train?',
    a: 'Open daily, 7 AM–11 PM.',
  },
  {
    q: 'How do I reserve a time?',
    a: <>Tap Book Now or email <EmailLink /> with the day, time, and number of players and we'll confirm.</>,
  },
  {
    q: 'Can my kid train here?',
    a: 'Players under 18 need an adult 18 or older present for the entire session. Waivers, including guardian permission for minors, are signed when you book.',
  },
  {
    q: 'Is anyone there?',
    a: "No staff on site. The gym is camera-monitored, and you'll get entry instructions after your booking is confirmed.",
  },
];

const tiers = [
  {
    label: 'Drop in',
    price: '$65',
    cadence: 'per reserved hour',
    title: 'Single session',
    items: ['50 minutes of court time', '10-minute turnover buffer', 'Shooting machine available by QR code', 'Up to 5 people on the floor', 'Confirm cancellation terms when booking'],
  },
  {
    label: 'Regulars',
    price: '$250',
    cadence: 'per month',
    title: 'Membership',
    items: ['5 reserved hours ($50/hr)', 'Save $75 vs. five drop-ins', '$55 per additional hour', 'Book up to 7 days early', 'Shooting machine included', 'Limited to 20 members'],
    featured: true,
  },
  {
    label: 'Coaches',
    price: '$45',
    cadence: 'per hour, standing block',
    title: 'Trainer block',
    items: ['Save $20/hr with a standing block', 'Same hours every week, reserved', 'Minimum 6 hours weekly', 'Bring your own clients and rates', 'Certificate of insurance required'],
  },
];

const rules = [
  ['01', 'Under 18', 'Players under 18 need an adult 18 or older present for the entire session. Waivers, including guardian permission for minors, are signed when you book.'],
  ['02', 'Five people', 'Five players maximum per session. Keep your group within the limit.'],
  ['03', 'Clear by :50', 'Your 50-minute court block ends at minute 50 so the next player can start clean. Please leave during the 10-minute turnover buffer.'],
  ['04', 'Leave it clean', 'Take your trash, tape, and bottles with you. No food on the court.'],
  ['05', 'Keep it down', 'Sessions end by 11 p.m. Please leave quietly. The parking lot is not a hangout.'],
] as const;

function BookingAction({ className = 'button button--orange' }: { className?: string }) {
  return (
    <a className={className} href={BOOKING_URL}>
      Book Now<Mail size={16} />
    </a>
  );
}

function EmailLink() {
  return <a className="email-inline" href={BOOKING_URL}>{BOOKING_EMAIL}</a>;
}

function Logo() {
  return (
    <a className="logo" href="/#top" aria-label="Blue Floor Basketball Gym home">
      <svg className="logo-mark" viewBox="0 0 40 40" aria-hidden="true">
        <circle cx="20" cy="20" r="17" />
        <path d="M3 20h34M20 3c-5 5-7 11-7 17s2 12 7 17m0-34c5 5 7 11 7 17s-2 12-7 17" />
      </svg>
      <span className="logo-wordmark"><strong>BLUE FLOOR</strong><small>BASKETBALL GYM</small></span>
    </a>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <>
      <a className="skip-link" href="#main">Skip to content</a>
      <div className="topline"><span className="topline__full">Private indoor court · Oakland · Open daily, 7 AM–11 PM</span><span className="topline__compact">Open daily, 7 AM–11 PM</span></div>
      <header className="site-header">
        <div className="shell nav-shell">
          <Logo />
          <nav className="desktop-nav" aria-label="Primary navigation">
            {navItems.map(([label, href]) => <a key={href} href={href}>{label}</a>)}
          </nav>
          <BookingAction className="button button--small button--orange desktop-cta" />
          <button className="menu-button" type="button" aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-expanded={menuOpen} onClick={() => setMenuOpen((open) => !open)}>
            {menuOpen ? <X /> : <Menu />}
          </button>
        </div>
        {menuOpen && (
          <nav className="mobile-nav" aria-label="Mobile navigation">
            {navItems.map(([label, href]) => <a key={href} href={href} onClick={() => setMenuOpen(false)}>{label}<ArrowUpRight size={17} /></a>)}
            <a href={BOOKING_URL} onClick={() => setMenuOpen(false)}>Book Now<ArrowRight size={17} /></a>
          </nav>
        )}
      </header>

      <main id="main">
        <section className="hero" id="top">
          <div className="hero__media">
            <img className="hero__image" src={HERO_IMAGE} alt="Private indoor basketball training bay with three hoops and blue cushioned floor in Oakland" width="1920" height="1080" fetchPriority="high" decoding="async" />
          </div>
          <div className="hero__veil" />
          <div className="hero__lines" aria-hidden="true" />
          <div className="shell hero__content">
            <div className="hero__copy">
              <h1 className="kicker hero__eyebrow">Private indoor basketball training · Oakland</h1>
              <p className="hero__headline">Your Court.<br /><span>Your Time.</span><br />Your Game.</p>
              <p className="hero__lede">A private indoor basketball training bay for individual shooting, skills workouts, private coaching, and small-group training—built around three hoops and a blue cushioned sport floor.</p>
              <div className="hero__actions">
                <BookingAction className="button button--orange button--large" />
                <a className="text-link" href="#space">See facility details <ArrowRight size={17} /></a>
              </div>
            </div>
            <aside className="hero__status" aria-label="Facility facts">
              <span className="live-dot" />
              <div><small>Hours</small><strong>Open daily, 7 AM–11 PM</strong></div>
            </aside>
          </div>
        </section>

        <section className="section section--light" id="how">
          <div className="shell">
            <div className="section-heading split-heading">
              <div>
                <p className="kicker kicker--dark">What the space is built for</p>
                <h2>Focused work.<br />Private space.</h2>
              </div>
              <p className="section-intro">Blue Floor is a private indoor basketball practice space—not a public full-court gym. Choose a focused window for the work you need to do.</p>
            </div>
            <div className="steps-grid">
              {trainingUses.map(({ number, icon: Icon, title, copy }) => (
                <article className="step-card" key={number}>
                  <span className="step-card__number">{number}</span><Icon aria-hidden="true" />
                  <h3>{title}</h3><p>{copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section section--space" id="space">
          <div className="shell space-layout">
            <div className="space-photo">
              <img className="space-photo__image" src={SPACE_IMAGE} alt="Blue Floor indoor basketball training court with a hoop, shooting machine, and blue cushioned floor" width="1920" height="1440" loading="lazy" decoding="async" />
              <div className="photo-tag"><span>Indoor training bay</span>3 hoops · shooting machine<br />Blue cushioned floor</div>
            </div>
            <div className="space-copy">
              <p className="kicker">Facility details</p>
              <h2>Built for reps.</h2>
              <p className="section-intro">A compact private indoor training bay for basketball practice. The space is built for individual shooting, skills workouts, private coaching, and small-group training—not full-court games.</p>
              <dl className="spec-list">
                <div><dt>Training surface</dt><dd>29 ft × 46 ft</dd></div>
                <div><dt>Hoops</dt><dd>3 hoops</dd></div>
                <div><dt>Flooring</dt><dd>Blue cushioned sport surface</dd></div>
                <div><dt>Equipment</dt><dd>Shooting machine</dd></div>
                <div><dt>Maximum occupancy</dt><dd>5 people</dd></div>
                <div><dt>Session format</dt><dd>50 min + 10 min buffer</dd></div>
              </dl>
            </div>
          </div>
        </section>

        <section className="section section--dark" id="pricing">
          <div className="shell">
            <div className="section-heading section-heading--dark">
              <p className="kicker">Pricing</p>
              <h2>Pick your<br />court time.</h2>
              <p className="section-intro">Straightforward options for a single indoor session, recurring personal practice, or a standing coach block.</p>
            </div>
            <div className="price-grid">
              {tiers.map((tier) => (
                <article className={`price-card ${tier.featured ? 'price-card--featured' : ''}`} key={tier.title}>
                  {tier.featured && <span className="price-card__flag">Most popular</span>}
                  <p className="price-card__label">{tier.label}</p>
                  <p className="price-card__price">{tier.price}</p>
                  <p className="price-card__cadence">{tier.cadence}</p>
                  <h3>{tier.title}</h3>
                  <ul>{tier.items.map((item) => <li key={item}><Check size={15} />{item}</li>)}</ul>
                  <BookingAction className={`button ${tier.featured ? 'button--orange' : 'button--outline'}`} />
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section trainer-section" id="trainers">
          <div className="shell trainer-panel">
            <div className="trainer-panel__icon"><Dumbbell size={32} /></div>
            <div className="trainer-panel__copy">
              <p className="kicker">For private skills trainers</p>
              <h2>Private space.<br />Your clients.</h2>
              <p>Set a standing indoor training block, bring your own clients, and run a focused session without competing for a public court. Trainers set their own rates and keep what they charge.</p>
              <div className="trainer-terms"><span>6 hrs/week minimum</span><span>$45/hr</span><span>Book 30 days ahead</span><span>COI required</span></div>
            </div>
            <BookingAction className="button button--cream button--large" />
          </div>
        </section>

        <section className="section section--cream" id="rules">
          <div className="shell">
            <div className="section-heading split-heading">
              <div><p className="kicker kicker--dark">Read before you visit</p><h2>Five rules.<br />That is it.</h2></div>
              <p className="section-intro">An unstaffed private facility only works if everyone treats the next session with respect. Read these before every visit.</p>
            </div>
            <div className="rules-grid">
              {rules.map(([number, title, copy]) => <article className="rule-card" key={number}><span>{number}</span><h3>{title}</h3><p>{copy}</p></article>)}
            </div>
          </div>
        </section>

        <section className="section section--light" id="faq">
          <div className="shell faq-layout">
            <div className="faq-title">
              <p className="kicker kicker--dark">Rental questions</p>
              <h2>Know before<br />you train.</h2>
              <p>Details customers need before reserving a private basketball session.</p>
            </div>
            <div className="faq-list">
              {faqs.map((faq, index) => <details key={faq.q} open={index === 0}><summary><span>{faq.q}</span><span className="faq-plus" aria-hidden="true">+</span></summary><p>{faq.a}</p></details>)}
            </div>
          </div>
        </section>

        <section className="section book-section" id="book">
          <div className="book-section__court-lines" aria-hidden="true" />
          <div className="shell book-layout">
            <div className="book-copy">
              <p className="kicker">Private practice space in Oakland</p>
              <h2>Make your next<br />court time count.</h2>
              <p>Tap Book Now with the day, time, and number of players and we'll confirm your session.</p>
              <div className="book-facts">
                <span><Clock3 size={18} />Open daily, 7 AM–11 PM</span>
                <span><Target size={18} /><strong>3 hoops</strong> indoor reps</span>
                <span><Users size={18} /><strong>5 people</strong> maximum</span>
                <span><ShieldCheck size={18} /><strong>Waivers</strong> signed when booking</span>
              </div>
            </div>
            <div className="booking-card">
              <div className="booking-card__heading"><Mail size={21} /><span>Reserve a session</span></div>
              <h3>Book Now</h3>
              <p>Private indoor court time for individual shooting, skills work, coaching, or a small group.</p>
              <BookingAction className="button button--orange button--large button--full" />
              <p className="booking-card__note">Online booking is coming soon.</p>
            </div>
          </div>
          <div className="shell find-us">
            <div className="find-us__copy">
              <p className="kicker">Find us</p>
              <h3>Blue Floor<br />Basketball Gym</h3>
              <address><MapPin size={18} />5960 Telegraph Ave<br />Oakland, CA 94609</address>
              <a className="find-us__email" href={BOOKING_URL}><Mail size={17} />{BOOKING_EMAIL}</a>
              <p className="find-us__hours"><Clock3 size={17} />Open daily, 7 AM–11 PM</p>
            </div>
            <div className="find-us__map"><iframe title="Blue Floor Basketball Gym location at 5960 Telegraph Ave, Oakland" src={MAP_EMBED_URL} loading="lazy" referrerPolicy="no-referrer-when-downgrade" /></div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="shell footer-grid">
          <div>
            <Logo />
            <p className="footer-tagline">Private indoor basketball training.</p>
            <address className="footer-contact">5960 Telegraph Ave<br />Oakland, CA 94609<br /><a href={BOOKING_URL}>{BOOKING_EMAIL}</a><br />Open daily, 7 AM–11 PM</address>
          </div>
          <div><p className="footer-label">Explore</p>{navItems.map(([label, href]) => <a key={href} href={href}>{label}</a>)}</div>
          <div><p className="footer-label">Book a court</p><BookingAction className="button button--small button--outline" /></div>
          <p className="footer-legal">© {new Date().getFullYear()} Blue Floor Basketball Gym. Train at your own risk. Confirm visit instructions and gym requirements before your session.</p>
        </div>
      </footer>
    </>
  );
}

export default App;
